<script lang="ts">
  // Monochrome Lucide-style icons als inline SVG. Kein npm-Paket --
  // wir holen uns nur die Pfade die wir brauchen, das spart 100 kB
  // Bundle. Strichstärke 1.5, currentColor, klare Linien.
  //
  // Verwenden: <Icon name="zap" /> oder <Icon name="zap" size={18} />.
  //
  // Jedes Symbol hat GENAU EINE Bedeutung: docs/symbole.md. Ein neues
  // Symbol bekommt dort seine Zeile, sonst schlägt symbole.test.mjs an.

  export type IconName =
    | "server" | "globe" | "hdd" | "shield" | "checks" | "droplet"
    | "zap" | "thermo" | "wind" | "bulb" | "cloud" | "cloud-rain"
    | "cloud-snow" | "cloud-fog" | "sunset" | "door" | "move" | "play"
    | "sun" | "moon" | "power" | "poweroff" | "arrow-right" | "chevron-down" | "chevron-up"
    | "arrow-up-down" | "chevron-left" | "calendar" | "alert-tri" | "alert-octa" | "info"
    | "check-circle" | "clock" | "pencil"
    | "home" | "wrench" | "settings" | "dots-horizontal"
    | "folder" | "file" | "search" | "zoom-out" | "pause" | "signal"
    | "plus" | "trash" | "download" | "upload" | "rotate-ccw" | "rotate-cw" | "lock"
    | "lock-open"
    | "history" | "chevron-right" | "laptop" | "box"
    | "plug" | "plug-off" | "stop" | "terminal" | "filter" | "bell" | "bell-off" | "x"
    | "copy" | "eye" | "eye-off" | "gauge" | "database" | "camera"
    // Claude (16.10.2026): Strahlenkranz für den Bereich, Sprechblase für einen Chat
    | "claude" | "message" | "paperclip" | "sliders"
    // Konstruktionen (18.10.2026): Messen, Schnitt, Teile, Einpassen, Bild
    | "ruler" | "scissors" | "layers" | "maximize" | "crosshair" | "image" | "blend"
    // Laufende Schritte (20.10.2026): dreht sich, solange etwas läuft; belegte Kerne
    | "loader" | "cpu"
    // Drucken (20.10.2026): durchgestrichen = Teil wird nicht gedruckt
    | "printer" | "printer-off"
    // Ein Symbol, eine Bedeutung (20.10.2026, docs/symbole.md): bestätigen,
    // Container, Archiv, An-/Abmelden, Ansichten der Konstruktion, Bewegung,
    // Upgrade, Senden, Vorziehen, Automatik, Wächter, Recovery-Tool
    | "check" | "container" | "archive" | "archive-restore" | "log-in" | "log-out"
    | "axis-3d" | "focus" | "boxes" | "footprints" | "circle-arrow-up" | "send"
    | "fast-forward" | "workflow" | "activity" | "life-buoy"
    // Argus (28.10.2026): das Auge aus Argus' eigenem Favicon -- spitze
    // Mandel, GEFÜLLTE Pupille; anders als `eye` (sichtbar: runde Enden,
    // hohle Pupille). Tim: „ein Argus-Icon anstatt der Kamera, das könnte
    // schnell in die Irre führen".
    | "argus"
    // Drucken kompakt (30.10.2026, docs/mockups/drucken-kompakt.html): RFID
    // der Bambu-Spule, Druckplatte und die Seiten des Slicers (Struktur,
    // Tempo, Stützen, Multimaterial), Vorschlag zum Übernehmen
    | "rfid" | "build-plate" | "infill" | "speed" | "supports" | "palette" | "wand"
    // AMS-Ersatzspule (30.10.2026): eine gleiche Spule springt ein
    | "ersatz"
    // Wartung des 3D-Druckers (30.10.2026): je Bauteil ein Symbol --
    // Stange, Z-Spindel, Lüfter, Filament-Messer, PTFE-Schlauch,
    // Aktivkohlefilter, Riementrieb, Extruder, Düse/Hotend
    | "stange" | "spindel" | "luefter" | "messer" | "schlauch" | "kohlefilter" | "riemen"
    | "extruder" | "duese"
    // Neu beginnen (31.10.2026): Claude vergisst den Verlauf des Chats
    | "eraser";

  interface Props {
    name: IconName;
    size?: number;
    class?: string;
  }

  let { name, size = 14, class: cls = "" }: Props = $props();
</script>

<svg
  width={size}
  height={size}
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
  class={cls}
  aria-hidden="true"
>
  {#if name === "server"}
    <rect x="2" y="3" width="20" height="8" rx="2"/>
    <rect x="2" y="13" width="20" height="8" rx="2"/>
    <line x1="6" y1="7" x2="6.01" y2="7"/>
    <line x1="6" y1="17" x2="6.01" y2="17"/>
  {:else if name === "globe"}
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" x2="22" y1="12" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  {:else if name === "hdd"}
    <line x1="22" x2="2" y1="12" y2="12"/>
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
    <line x1="6" x2="6.01" y1="16" y2="16"/>
    <line x1="10" x2="10.01" y1="16" y2="16"/>
  {:else if name === "folder"}
    <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/>
  {:else if name === "file"}
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  {:else if name === "plus"}
    <path d="M12 5v14"/><path d="M5 12h14"/>
  {:else if name === "trash"}
    <path d="M3 6h18"/>
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
    <line x1="10" x2="10" y1="11" y2="17"/>
    <line x1="14" x2="14" y1="11" y2="17"/>
  {:else if name === "download"}
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" x2="12" y1="15" y2="3"/>
  {:else if name === "upload"}
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" x2="12" y1="3" y2="15"/>
  {:else if name === "rotate-cw"}
    <path d="M21 12a9 9 0 1 1-3-6.7L21 8"/>
    <path d="M21 3v5h-5"/>
  {:else if name === "database"}
    <!-- Datenbank: drei gestapelte Scheiben (Server-Seite, Sektion
         Datenbank und Tabellen). -->
    <ellipse cx="12" cy="5" rx="9" ry="3"/>
    <path d="M3 5v14a9 3 0 0 0 18 0V5"/>
    <path d="M3 12a9 3 0 0 0 18 0"/>
  {:else if name === "gauge"}
    <!-- Messuhr: einmessen, kalibrieren. (Der Rück-Pfeil heißt
         „zurücksetzen" -- für „die Ruhe neu vermessen" passte er nicht,
         Tim 13.10.2026.) -->
    <path d="m12 14 4-4"/>
    <path d="M3.34 19a10 10 0 1 1 17.32 0"/>
  {:else if name === "rotate-ccw"}
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8"/>
    <path d="M3 3v5h5"/>
  {:else if name === "bell"}
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
  {:else if name === "bell-off"}
    <path d="M8.7 3A6 6 0 0 1 18 8a21.3 21.3 0 0 0 .6 5"/>
    <path d="M17 17H3s3-2 3-9a4.67 4.67 0 0 1 .3-1.7"/>
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
    <path d="m2 2 20 20"/>
  {:else if name === "filter"}
    <path d="M22 3H2l8 9.46V19l4 2v-8.54z"/>
  {:else if name === "lock"}
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  {:else if name === "lock-open"}
    <!-- Gleicher Koerper, Buegel offen: der Unterschied muss auch bei
         15px sofort lesbar sein, deshalb steht er ganz zur Seite. -->
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
  {:else if name === "history"}
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8"/>
    <path d="M3 3v5h5"/>
    <path d="M12 7v5l4 2"/>
  {:else if name === "chevron-right"}
    <path d="m9 18 6-6-6-6"/>
  {:else if name === "plug"}
    <path d="M12 22v-5" /><path d="M9 8V2" /><path d="M15 8V2" />
    <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8z" />
  {:else if name === "plug-off"}
    <path d="M12 22v-3" /><path d="M9 8V2" />
    <path d="M18 8v5a4 4 0 0 1-2.5 3.7" />
    <path d="M6 8v5a4 4 0 0 0 4 4h1" />
    <path d="M3 3l18 18" />
  {:else if name === "box"}
    <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9z"/>
    <path d="m4 7.5 8 4.5 8-4.5M12 12v9"/>
  {:else if name === "laptop"}
    <path d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v11H3z"/>
    <path d="M2 19h20a0 0 0 0 1 0 0 1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z"/>
  {:else if name === "shield"}
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1 18.94 18.94 0 0 0 7-1.95 18.94 18.94 0 0 0 7 1.95 1 1 0 0 1 1 1z"/>
  {:else if name === "checks"}
    <path d="m3 17 2 2 4-4"/>
    <path d="m3 7 2 2 4-4"/>
    <path d="M13 6h8"/><path d="M13 12h8"/><path d="M13 18h8"/>
  {:else if name === "droplet"}
    <path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z"/>
  {:else if name === "zap"}
    <path d="M13 2 3 14h9l-1 8 10-12h-9z"/>
  {:else if name === "thermo"}
    <path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z"/>
  {:else if name === "wind"}
    <path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2"/>
    <path d="M9.6 4.6A2 2 0 1 1 11 8H2"/>
    <path d="M12.6 19.4A2 2 0 1 0 14 16H2"/>
  {:else if name === "bulb"}
    <path d="M9 18h6"/><path d="M10 22h4"/>
    <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
  {:else if name === "cloud"}
    <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/>
  {:else if name === "cloud-rain"}
    <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/>
    <path d="M16 14v6"/><path d="M8 14v6"/><path d="M12 16v6"/>
  {:else if name === "cloud-snow"}
    <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/>
    <path d="M8 15h.01"/><path d="M8 19h.01"/>
    <path d="M12 17h.01"/><path d="M12 21h.01"/>
    <path d="M16 15h.01"/><path d="M16 19h.01"/>
  {:else if name === "cloud-fog"}
    <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/>
    <path d="M5 17h14"/><path d="M5 21h14"/>
  {:else if name === "sunset"}
    <path d="M12 10V2"/><path d="m4.93 10.93 1.41 1.41"/>
    <path d="M2 18h2"/><path d="M20 18h2"/>
    <path d="m19.07 10.93-1.41 1.41"/><path d="M22 22H2"/>
    <path d="m16 6-4 4-4-4"/><path d="M16 18a4 4 0 0 0-8 0"/>
  {:else if name === "door"}
    <path d="M13 4h3a2 2 0 0 1 2 2v14"/>
    <path d="M2 20h3"/><path d="M13 20h9"/><path d="M10 12v.01"/>
    <path d="M13 4.56v16.16a1 1 0 0 1-1.24.97L5.76 20.2a2 2 0 0 1-1.52-1.94V5.56a2 2 0 0 1 1.48-1.93l6-1.58A1 1 0 0 1 13 3.01z"/>
  {:else if name === "claude"}
    <!-- einfarbiger Strahlenkranz, Strich wie alle Symbole -->
    <path d="M12 3v5.5M12 15.5V21M3 12h5.5M15.5 12H21M5.6 5.6l3.9 3.9M14.5 14.5l3.9 3.9M18.4 5.6l-3.9 3.9M9.5 14.5l-3.9 3.9"/>
  {:else if name === "paperclip"}
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
  {:else if name === "sliders"}
    <path d="M21 4h-7M10 4H3M21 12h-9M8 12H3M21 20h-5M12 20H3M14 2v4M8 10v4M16 18v4"/>
  {:else if name === "message"}
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  {:else if name === "move"}
    <circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/>
    <circle cx="12" cy="19" r="1"/><circle cx="5" cy="12" r="1"/>
    <circle cx="19" cy="12" r="1"/>
  {:else if name === "play"}
    <circle cx="12" cy="12" r="10"/>
    <polygon points="10 8 16 12 10 16 10 8"/>
  {:else if name === "sun"}
    <circle cx="12" cy="12" r="4"/>
    <path d="M12 2v2"/><path d="M12 20v2"/>
    <path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/>
    <path d="M2 12h2"/><path d="M20 12h2"/>
    <path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/>
  {:else if name === "moon"}
    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z"/>
  {:else if name === "power"}
    <path d="M12 2v10"/>
    <path d="M18.4 6.6a9 9 0 1 1-12.77.04"/>
  {:else if name === "poweroff"}
    <path d="M18.36 6.64A9 9 0 0 1 20.77 15"/>
    <path d="M6.16 6.16a9 9 0 1 0 12.68 12.68"/>
    <path d="M12 2v4"/><path d="m2 2 20 20"/>
  {:else if name === "arrow-right"}
    <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
  {:else if name === "chevron-down"}
    <path d="m6 9 6 6 6-6"/>
  {:else if name === "chevron-up"}
    <path d="m6 15 6-6 6 6"/>
  {:else if name === "arrow-up-down"}
    <path d="m21 16-4 4-4-4"/><path d="M17 20V4"/>
    <path d="m3 8 4-4 4 4"/><path d="M7 4v16"/>
  {:else if name === "chevron-left"}
    <path d="m15 18-6-6 6-6"/>
  {:else if name === "calendar"}
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  {:else if name === "alert-tri"}
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
    <path d="M12 9v4"/><path d="M12 17h.01"/>
  {:else if name === "alert-octa"}
    <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/>
    <line x1="12" x2="12" y1="8" y2="12"/>
    <line x1="12" x2="12.01" y1="16" y2="16"/>
  {:else if name === "info"}
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 16v-4"/>
    <path d="M12 8h.01"/>
  {:else if name === "check-circle"}
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <path d="m9 11 3 3L22 4"/>
  {:else if name === "clock"}
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  {:else if name === "pencil"}
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
  {:else if name === "home"}
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  {:else if name === "wrench"}
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
  {:else if name === "settings"}
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
    <circle cx="12" cy="12" r="3"/>
  {:else if name === "dots-horizontal"}
    <circle cx="12" cy="12" r="1"/>
    <circle cx="19" cy="12" r="1"/>
    <circle cx="5" cy="12" r="1"/>
  {:else if name === "search"}
    <circle cx="11" cy="11" r="7"/>
    <line x1="16.65" y1="16.65" x2="21" y2="21"/>
  {:else if name === "zoom-out"}
    <circle cx="11" cy="11" r="7"/>
    <line x1="16.65" y1="16.65" x2="21" y2="21"/>
    <line x1="8" y1="11" x2="14" y2="11"/>
  {:else if name === "pause"}
    <circle cx="12" cy="12" r="10"/>
    <line x1="10" y1="9" x2="10" y2="15"/>
    <line x1="14" y1="9" x2="14" y2="15"/>
  {:else if name === "signal"}
    <path d="M2 20h.01"/>
    <path d="M7 20v-4"/>
    <path d="M12 20v-8"/>
    <path d="M17 20V8"/>
    <path d="M22 4v16"/>
  {:else if name === "x"}
    <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
  {:else if name === "stop"}
    <!-- Lauf abbrechen: das Quadrat jedes Abspielers. -->
    <rect x="6" y="6" width="12" height="12" rx="1.5"/>
  {:else if name === "terminal"}
    <!-- Ausgabe eines Laufs -->
    <rect x="3" y="4" width="18" height="16" rx="2"/><path d="m7 9 3 3-3 3"/><path d="M12 15h5"/>
  {:else if name === "copy"}
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
  {:else if name === "eye"}
    <path d="M2.06 12.35a1 1 0 0 1 0-.7 10.75 10.75 0 0 1 19.88 0 1 1 0 0 1 0 .7 10.75 10.75 0 0 1-19.88 0"/><circle cx="12" cy="12" r="3"/>
  {:else if name === "argus"}
    <!-- Argus' Favicon (viewBox 32) auf 24 übertragen: Mandel mit spitzen
         Ecken, Pupille gefüllt -->
    <path d="M3 12c2.4-4 5.4-6 9-6s6.6 2 9 6c-2.4 4-5.4 6-9 6s-6.6-2-9-6Z"/><circle cx="12" cy="12" r="2.6" fill="currentColor"/>
  {:else if name === "camera"}
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/>
  {:else if name === "ruler"}
    <path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z"/>
    <path d="m14.5 12.5 2-2"/><path d="m11.5 9.5 2-2"/><path d="m8.5 6.5 2-2"/><path d="m17.5 15.5 2-2"/>
  {:else if name === "scissors"}
    <circle cx="6" cy="6" r="3"/><path d="M8.12 8.12 12 12"/><path d="M20 4 8.12 15.88"/>
    <circle cx="6" cy="18" r="3"/><path d="M14.8 14.8 20 20"/>
  {:else if name === "layers"}
    <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"/>
    <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"/><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"/>
  {:else if name === "maximize"}
    <path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/>
    <path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>
  {:else if name === "crosshair"}
    <circle cx="12" cy="12" r="10"/><line x1="22" x2="18" y1="12" y2="12"/><line x1="6" x2="2" y1="12" y2="12"/>
    <line x1="12" x2="12" y1="6" y2="2"/><line x1="12" x2="12" y1="22" y2="18"/>
  {:else if name === "image"}
    <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/>
    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
  {:else if name === "blend"}
    <circle cx="9" cy="9" r="7"/><circle cx="15" cy="15" r="7"/>
  {:else if name === "loader"}
    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
  {:else if name === "cpu"}
    <rect width="16" height="16" x="4" y="4" rx="2"/><rect width="6" height="6" x="9" y="9" rx="1"/><path d="M15 2v2"/><path d="M15 20v2"/><path d="M2 15h2"/><path d="M2 9h2"/><path d="M20 15h2"/><path d="M20 9h2"/><path d="M9 2v2"/><path d="M9 20v2"/>
  {:else if name === "printer" || name === "printer-off"}
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6"/><rect x="6" y="14" width="12" height="8" rx="1"/>
    {#if name === "printer-off"}<path d="m2 2 20 20"/>{/if}
  {:else if name === "eye-off"}
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c4.97 0 8.53 3.13 9.94 6.65a1 1 0 0 1 0 .7 10.75 10.75 0 0 1-1.44 2.49"/><path d="M14.08 14.16a3 3 0 0 1-4.17-4.17"/><path d="M17.48 17.5a10.75 10.75 0 0 1-15.42-5.15 1 1 0 0 1 0-.7 10.75 10.75 0 0 1 4.45-5.14"/><path d="m2 2 20 20"/>
  {:else if name === "check"}
    <!-- Bestätigen: speichern, übernehmen, gelesen, fertig (ein Haken).
         Die Liste mit Haken (`checks`) heißt Aufgaben. -->
    <path d="M20 6 9 17l-5-5"/>
  {:else if name === "container"}
    <path d="M22 7.7c0-.6-.4-1.2-.8-1.5l-6.3-3.9a1.72 1.72 0 0 0-1.7 0l-10.3 6c-.5.2-.9.8-.9 1.4v6.6c0 .5.4 1.2.8 1.5l6.3 3.9a1.72 1.72 0 0 0 1.7 0l10.3-6c.5-.3.9-1 .9-1.5Z"/>
    <path d="M10 21.9V14L2.1 9.1"/><path d="m10 14 11.9-6.9"/><path d="M14 19.8v-8.1"/><path d="M18 17.5V9.4"/>
  {:else if name === "archive"}
    <rect width="20" height="5" x="2" y="3" rx="1"/><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/><path d="M10 12h4"/>
  {:else if name === "archive-restore"}
    <rect width="20" height="5" x="2" y="3" rx="1"/><path d="M4 8v11a2 2 0 0 0 2 2h2"/><path d="M20 8v11a2 2 0 0 1-2 2h-2"/>
    <path d="m9 15 3-3 3 3"/><path d="M12 12v9"/>
  {:else if name === "log-in"}
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><path d="m10 17 5-5-5-5"/><path d="M15 12H3"/>
  {:else if name === "log-out"}
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="m16 17 5-5-5-5"/><path d="M21 12H9"/>
  {:else if name === "axis-3d"}
    <path d="M4 4v16h16"/><path d="m4 20 7-7"/>
  {:else if name === "focus"}
    <circle cx="12" cy="12" r="3"/><path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/>
    <path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
  {:else if name === "boxes"}
    <path d="M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5.5l-5-3-4.03 2.42Z"/>
    <path d="m7 16.5-4.74-2.85"/><path d="m7 16.5 5-3"/><path d="M7 16.5v5.17"/>
    <path d="M12 13.5V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 10.5l-5 3Z"/>
    <path d="m17 16.5-5-3"/><path d="m17 16.5 4.74-2.85"/><path d="M17 16.5v5.17"/>
    <path d="M7.97 4.42A2 2 0 0 0 7 6.13v4.37l5 3 5-3V6.13a2 2 0 0 0-.97-1.71l-3-1.8a2 2 0 0 0-2.06 0l-3 1.8Z"/>
    <path d="M12 8 7.26 5.15"/><path d="m12 8 4.74-2.85"/><path d="M12 13.5V8"/>
  {:else if name === "footprints"}
    <path d="M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5.5c0 3.11-2 5.66-2 8.68V16a2 2 0 1 1-4 0Z"/>
    <path d="M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.49-6-4.5-6C14.63 6 14 7.8 14 9.5c0 3.11 2 5.66 2 8.68V20a2 2 0 1 0 4 0Z"/>
    <path d="M16 17h4"/><path d="M4 13h4"/>
  {:else if name === "circle-arrow-up"}
    <circle cx="12" cy="12" r="10"/><path d="m16 12-4-4-4 4"/><path d="M12 16V8"/>
  {:else if name === "send"}
    <path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/>
  {:else if name === "fast-forward"}
    <polygon points="13 19 22 12 13 5 13 19"/><polygon points="2 19 11 12 2 5 2 19"/>
  {:else if name === "workflow"}
    <rect width="8" height="8" x="3" y="3" rx="2"/><path d="M7 11v4a2 2 0 0 0 2 2h4"/><rect width="8" height="8" x="13" y="13" rx="2"/>
  {:else if name === "activity"}
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
  {:else if name === "life-buoy"}
    <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/>
    <path d="m4.93 4.93 4.24 4.24"/><path d="m14.83 9.17 4.24-4.24"/><path d="m14.83 14.83 4.24 4.24"/><path d="m9.17 14.83-4.24 4.24"/>
  {:else if name === "ersatz"}
    <!-- Zwei Pfeile im Kreislauf: diese Spule hat eine gleiche als Ersatz
         (AMS Auto Refill) -->
    <path d="m17 2 4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/>
    <path d="m7 22-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/>
  {:else if name === "rfid"}
    <!-- Kontaktlos-Zeichen: die Spule trägt einen RFID-Chip, der Drucker
         liest sie selbst -->
    <path d="M6 8.32a7.43 7.43 0 0 1 0 7.36"/><path d="M9.46 6.21a11.76 11.76 0 0 1 0 11.58"/>
    <path d="M12.91 4.1a15.91 15.91 0 0 1 .01 15.8"/><path d="M16.37 2a20.16 20.16 0 0 1 0 20"/>
  {:else if name === "build-plate"}
    <!-- Druckplatte schräg von vorn, mit Struktur -->
    <path d="M2.5 15.5 7 9h10l4.5 6.5z"/><path d="M2.5 15.5v2h19v-2"/><path d="M8.5 12.5h7"/>
  {:else if name === "infill"}
    <!-- Struktur: Wände (zwei Umrisse) und schräge Füllung -->
    <rect x="3" y="3" width="18" height="18" rx="2"/><rect x="6.5" y="6.5" width="11" height="11" rx="1"/>
    <path d="m6.5 12 5.5-5.5"/><path d="m6.5 17.5 11-11"/><path d="m12 17.5 5.5-5.5"/>
  {:else if name === "speed"}
    <!-- Tacho als Kreis: Tempo. Nicht `gauge` (Halbbogen = kalibrieren). -->
    <path d="M15.6 2.7a10 10 0 1 0 5.7 5.7"/><circle cx="12" cy="12" r="2"/><path d="M13.4 10.6 19 5"/>
  {:else if name === "supports"}
    <!-- Stützen: ein Block auf Säulen über der Platte -->
    <rect x="3" y="3.5" width="18" height="4" rx="1"/><path d="M6.5 7.5V20"/><path d="M12 7.5V20"/><path d="M17.5 7.5V20"/><path d="M3 20.5h18"/>
  {:else if name === "palette"}
    <path d="M12 22a1 1 0 0 1 0-20 10 9 0 0 1 10 9 5 5 0 0 1-5 5h-2.25a1.75 1.75 0 0 0-1.4 2.8l.3.4a1.75 1.75 0 0 1-1.4 2.8z"/>
    <circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/>
    <circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/>
  {:else if name === "eraser"}
    <!-- Radiergummi: der Chat beginnt neu, Claude vergisst den Verlauf davor -->
    <path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21"/><path d="M22 21H7"/><path d="m5 11 9 9"/>
  {:else if name === "wand"}
    <!-- Zauberstab: ein Vorschlag, der eine Einstellung verbessert -->
    <path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72"/>
    <path d="m14 7 3 3"/><path d="M5 6v4"/><path d="M19 14v4"/><path d="M10 2v2"/><path d="M7 8H3"/><path d="M21 16h-4"/><path d="M11 3H9"/>
  {:else if name === "stange"}
    <!-- Stange mit Lager: Kohlefaser- oder Führungsstange einer Achse -->
    <path d="M2 10.5h6.5M15.5 10.5H22M2 13.5h6.5M15.5 13.5H22M2 10.5v3M22 10.5v3"/>
    <rect x="8.5" y="7.5" width="7" height="9" rx="1.5"/>
  {:else if name === "spindel"}
    <!-- Gewindestange: die Z-Spindel, die das Bett hebt -->
    <path d="M10 2h4"/><path d="M12 2v20"/>
    <path d="m9.5 7 5-2"/><path d="m9.5 11 5-2"/><path d="m9.5 15 5-2"/><path d="m9.5 19 5-2"/>
  {:else if name === "luefter"}
    <!-- Lüfter: Flügelrad -->
    <path d="M10.827 16.379a6.082 6.082 0 0 1-8.618-7.002l5.412 1.45a6.082 6.082 0 0 1 7.002-8.618l-1.45 5.412a6.082 6.082 0 0 1 8.618 7.002l-5.412-1.45a6.082 6.082 0 0 1-7.002 8.618l1.45-5.412Z"/>
    <path d="M12 12v.01"/>
  {:else if name === "messer"}
    <!-- Klinge: das Filament-Messer am Druckkopf -->
    <path d="M11 16.586V19a1 1 0 0 1-1 1H2L18.37 3.63a1 1 0 1 1 3 3l-9.663 9.663a1 1 0 0 1-1.414 0L8 14"/>
  {:else if name === "schlauch"}
    <!-- Gebogener Schlauch mit Wandstärke: der PTFE-Schlauch -->
    <path d="M3 21v-7a8 8 0 0 1 8-8h10"/><path d="M7 21v-7a4 4 0 0 1 4-4h10"/>
    <path d="M3 21h4"/><path d="M21 6v4"/>
  {:else if name === "kohlefilter"}
    <!-- Filterkartusche mit Wabe: der Aktivkohlefilter -->
    <rect x="4" y="3" width="16" height="18" rx="2"/>
    <path d="M8 7.5h.01M12 7.5h.01M16 7.5h.01M10 10.5h.01M14 10.5h.01M8 13.5h.01M12 13.5h.01M16 13.5h.01M10 16.5h.01M14 16.5h.01"/>
  {:else if name === "riemen"}
    <!-- Riemen um zwei Rollen: der Riementrieb (Riemen, Umlenkrollen) -->
    <rect x="2" y="7" width="20" height="10" rx="5"/><circle cx="7" cy="12" r="2.5"/><circle cx="17" cy="12" r="2.5"/>
  {:else if name === "extruder"}
    <!-- Zwei Antriebsräder, dazwischen das Filament: der Extruder -->
    <circle cx="7.5" cy="12" r="4"/><circle cx="16.5" cy="12" r="4"/><path d="M12 2v20"/>
    <path d="M7.5 12h.01M16.5 12h.01"/>
  {:else if name === "duese"}
    <!-- Heizblock mit Düse: Düse und Hotend -->
    <path d="M12 2v3"/><rect x="6" y="5" width="12" height="8" rx="1"/><path d="M10 13v2l1 3h2l1-3v-2"/>
    <path d="M12 21v.01"/>
  {/if}
</svg>
